import { useState, useCallback, useMemo, useRef } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Typography, IconButton,
  Checkbox, FormControlLabel, Chip, Tabs, Tab, Divider,
  Autocomplete, Radio, RadioGroup, FormControl, FormLabel,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon,
  ContentCopy as CloneIcon, SwapHoriz as MoveIcon, MergeType as MergeIcon,
  Lock as LockIcon, LockOpen as UnlockIcon,
  Upload as UploadIcon, ExpandMore as ExpandMoreIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import type { SavedSearch } from '../../api/types';
import { AdminListPanel } from './AdminListPanel';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import { ConfirmDialog } from '../../shared/components/Dialogs';
import { useAuth } from '../auth/useAuth';
import type { User } from '../../api/types';

/** Extract details blob (schedule JSON often holds contactInfo, addressInfo) */
const det = (u: User) => {
  try {
    const d = (u as Record<string, unknown>).details;
    if (d && typeof d === 'object') return d as Record<string, unknown>;
    if (typeof d === 'string') return JSON.parse(d) as Record<string, unknown>;
  } catch { /* ignore */ }
  return {} as Record<string, unknown>;
};

const sched = (u: User) => {
  try {
    const s = (u as Record<string, unknown>).schedule;
    if (s && typeof s === 'object') return s as Record<string, unknown>;
    if (typeof s === 'string') return JSON.parse(s) as Record<string, unknown>;
  } catch { /* ignore */ }
  return {} as Record<string, unknown>;
};

const contactOf = (u: User, field: string): string => {
  const s = sched(u);
  const ci = (s.contactInfo ?? s) as Record<string, unknown>;
  return (ci[field] ?? '') as string;
};

/** Parse DICOM Person Name: Last^First^Middle^Prefix^Suffix */
const parseDicomPN = (name: string) => {
  const parts = (name || '').split('^');
  return { last: parts[0] || '', first: parts[1] || '', middle: parts[2] || '', prefix: parts[3] || '', suffix: parts[4] || '' };
};

/** Build DICOM Person Name from parts, trimming trailing empties */
const buildDicomPN = (last: string, first: string, middle: string, prefix: string, suffix: string): string => {
  const parts = [last, first, middle, prefix, suffix];
  while (parts.length > 0 && !parts[parts.length - 1]) parts.pop();
  return parts.join('^');
};

/** Nice display name from DICOM PN (for grid display, not editing) */
const niceNameFromPN = (pn: string) => {
  const p = parseDicomPN(pn);
  return [p.prefix, p.first, p.middle, p.last, p.suffix].filter(Boolean).join(' ');
};

const LANGUAGES = [
  { value: '', label: '— Default —' },
  { value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' }, { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' }, { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' }, { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' }, { value: 'ar', label: 'Arabic' },
  { value: 'ru', label: 'Russian' }, { value: 'hi', label: 'Hindi' },
  { value: 'vi', label: 'Vietnamese' },
];

const CONTACT_METHODS = [
  { value: '', label: '— None —' },
  { value: 'office', label: 'Office Phone' }, { value: 'mobile', label: 'Mobile Phone' },
  { value: 'home', label: 'Home Phone' }, { value: 'pager', label: 'Pager' },
  { value: 'fax', label: 'Fax' }, { value: 'email', label: 'Email' },
];

/**
 * Grid columns — matches GWT UserListPanel default columns:
 * ID, Name, User Role, Office Phone, Mobile Phone, Email, Active?, Locked?
 */
const columns: GridColDef[] = [
  { field: 'userId', headerName: 'ID', width: 100,
    valueGetter: (_v: unknown, row: User) => (row as Record<string, unknown>).userId ?? row.user },
  { field: 'userName', headerName: 'Name', flex: 1.5, minWidth: 150,
    valueGetter: (_v: unknown, row: User) => {
      const r = row as Record<string, unknown>;
      const nice = r.niceName as string | undefined;
      if (nice) return nice;
      const raw = (r.userName ?? row.name ?? '') as string;
      return raw.includes('^') ? niceNameFromPN(raw) : raw;
    } },
  { field: 'userType', headerName: 'Role', width: 120,
    valueGetter: (_v: unknown, row: User) => (row as Record<string, unknown>).userType ?? row.role },
  { field: 'siteid', headerName: 'Site', width: 100,
    valueGetter: (_v: unknown, row: User) => (row as Record<string, unknown>).siteid ?? row.siteName },
  { field: 'officePhone', headerName: 'Office Phone', width: 120,
    valueGetter: (_v: unknown, row: User) => contactOf(row, 'office') },
  { field: 'mobilePhone', headerName: 'Mobile Phone', width: 120,
    valueGetter: (_v: unknown, row: User) => contactOf(row, 'mobile') },
  { field: 'email', headerName: 'Email', width: 160,
    valueGetter: (_v: unknown, row: User) => contactOf(row, 'email') },
  { field: 'dicomAe', headerName: 'DICOM AE', width: 100 },
  { field: 'active', headerName: 'Active?', width: 70,
    renderCell: (params) => {
      const a = (params.row as Record<string, unknown>).active;
      const active = a === undefined || a === true || a === 'true';
      return <Chip label={active ? 'Yes' : 'No'} size="small" color={active ? 'success' : 'default'} sx={{ fontSize: 10, height: 18 }} />;
    },
  },
  { field: 'state', headerName: 'Locked?', width: 70,
    renderCell: (params) => {
      const locked = (params.row as Record<string, unknown>).state === 'LOCKED';
      return locked ? <Chip label="Yes" size="small" color="error" sx={{ fontSize: 10, height: 18 }} /> : null;
    },
  },
  {
    field: 'actions',
    headerName: '',
    width: 192,
    sortable: false,
    renderCell: () => null,
  },
];

/**
 * Users admin tab — UserListPanel equivalent.
 *
 * Grid: ID, Name, Role, Site, Office Phone, Mobile Phone, Email, DICOM AE, Active?
 * Toolbar: Add User, Delete, Show Inactive, Refresh.
 * Edit: Tabbed dialog — General, Contact Info, DICOM, Address.
 */
export const UsersPanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState<User | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [cloneSource, setCloneSource] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [moveTarget, setMoveTarget] = useState<User | null>(null);
  const [mergeTarget, setMergeTarget] = useState<User | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.users.all, search, showInactive],
    queryFn: () => api.getUsersPaged(0, 500, {
      searchName: search || undefined,
      searchInactive: showInactive || undefined,
    }),
    staleTime: 30_000,
  });

  const users = useMemo(() => {
    const all = data?.records ?? [];
    if (!search) return all;
    const s = search.toLowerCase();
    return all.filter(
      (u) =>
        u.user.toLowerCase().includes(s) ||
        u.name.toLowerCase().includes(s) ||
        ((u as Record<string, unknown>).userId as string ?? '').toLowerCase().includes(s) ||
        ((u as Record<string, unknown>).userName as string ?? '').toLowerCase().includes(s) ||
        (u.role ?? '').toLowerCase().includes(s) ||
        (u.siteName ?? '').toLowerCase().includes(s),
    );
  }, [data, search]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
  }, [queryClient]);

  const [deleteRefs, setDeleteRefs] = useState<{ studies: number; orders: number; reports: number } | null>(null);

  // When deleteTarget changes, check for references
  const handleDeleteAttempt = useCallback(async (user: User) => {
    try {
      const refs = await api.hasUserReferences(user.objKey);
      setDeleteRefs(refs.hasReferences ? refs : null);
    } catch {
      setDeleteRefs(null);
    }
    setDeleteTarget(user);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const siteId = (deleteTarget as Record<string, unknown>).siteid as string ?? deleteTarget.siteId ?? '';
      const userId = (deleteTarget as Record<string, unknown>).userId as string ?? deleteTarget.user;
      await api.deleteUser(siteId, userId);
      pushSuccess('User deleted successfully');
      refresh();
    } catch (e) {
      pushError(`Failed to delete user: ${e instanceof Error ? e.message : String(e)}`);
    }
    setDeleteTarget(null);
    setDeleteRefs(null);
  }, [deleteTarget, refresh]);

  return (
    <>
      <AdminListPanel
        title="Users"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        onAdd={() => setShowAddDialog(true)}
        statusText={`${users.length} users`}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} sx={{ p: 0.25 }} />}
            label={<Typography sx={{ fontSize: 11 }}>Show Inactive</Typography>}
            sx={{ ml: 0 }}
          />
        </Box>
        <DataGrid
          rows={users}
          columns={columns.map((col) =>
            col.field === 'actions'
              ? {
                  ...col,
                  renderCell: (params) => (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => setEditUser(params.row as User)} title="Edit" sx={{ width: 28, height: 28 }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setCloneSource(params.row as User)} title="Clone" sx={{ width: 28, height: 28 }}>
                        <CloneIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setMoveTarget(params.row as User)} title="Move to Site" sx={{ width: 28, height: 28 }}>
                        <MoveIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setMergeTarget(params.row as User)} title="Merge Users" sx={{ width: 28, height: 28 }}>
                        <MergeIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          const u = params.row as User;
                          const isLocked = (u as Record<string, unknown>).state === 'LOCKED';
                          try {
                            if (isLocked) {
                              await api.removeLockFromUser(u.objKey);
                              pushSuccess(`User "${u.name}" unlocked`);
                            } else {
                              await api.putLockOnUser(u.objKey);
                              pushSuccess(`User "${u.name}" locked`);
                            }
                            refresh();
                          } catch (e) {
                            pushError(`Failed to ${isLocked ? 'unlock' : 'lock'} user: ${e instanceof Error ? e.message : String(e)}`);
                          }
                        }}
                        title={(params.row as Record<string, unknown>).state === 'LOCKED' ? 'Unlock' : 'Lock'}
                        sx={{ width: 28, height: 28 }}
                      >
                        {(params.row as Record<string, unknown>).state === 'LOCKED'
                          ? <UnlockIcon sx={{ fontSize: 16 }} />
                          : <LockIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteAttempt(params.row as User)} title="Delete" sx={{ width: 28, height: 28 }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ),
                }
              : col,
          )}
          getRowId={(row) => (row as User).objKey}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          rowHeight={36}
          columnHeaderHeight={32}
          sx={{
            border: 'none',
            fontSize: 12,
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </AdminListPanel>

      {(editUser || showAddDialog || cloneSource) && (
        <UserDialog
          user={editUser}
          cloneFrom={cloneSource}
          open
          onClose={() => { setEditUser(null); setShowAddDialog(false); setCloneSource(null); }}
          onSave={() => { setEditUser(null); setShowAddDialog(false); setCloneSource(null); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          title="Delete User"
          message={deleteRefs
            ? `User "${deleteTarget.name}" has ${deleteRefs.studies} studies, ${deleteRefs.orders} orders, and ${deleteRefs.reports} reports. Are you sure you want to delete this user? This action cannot be undone.`
            : `Are you sure you want to delete user "${deleteTarget.name}" (${deleteTarget.user})? This action cannot be undone.`}
          destructive
          onConfirm={handleDelete}
          onCancel={() => { setDeleteTarget(null); setDeleteRefs(null); }}
        />
      )}

      {moveTarget && (
        <MoveUserDialog
          user={moveTarget}
          open
          onClose={() => setMoveTarget(null)}
          onSaved={() => { setMoveTarget(null); refresh(); }}
        />
      )}

      {mergeTarget && (
        <MergeUsersDialog
          user={mergeTarget}
          allUsers={users}
          open
          onClose={() => setMergeTarget(null)}
          onSaved={() => { setMergeTarget(null); refresh(); }}
        />
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * SignatureSection — signature image upload/remove for a user
 * ══════════════════════════════════════════════════════════════════════════ */
const SignatureSection = ({ userObjKey }: { userObjKey?: string }) => {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ dataUrl: string; mimeType: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data: sigData, isLoading: loadingSig } = useQuery({
    queryKey: ['signatureImage', userObjKey],
    queryFn: () => api.getSignatureImage(userObjKey!),
    enabled: !!userObjKey,
    staleTime: 30_000,
  });

  const currentDataUrl = sigData?.image && sigData?.mimeType
    ? `data:${sigData.mimeType};base64,${sigData.image}`
    : sigData?.image
      ? `data:image/png;base64,${sigData.image}`
      : null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userObjKey) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setPreview({ dataUrl, mimeType: file.type || 'image/png' });
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = useCallback(async () => {
    if (!preview || !userObjKey) return;
    setUploading(true);
    try {
      // Strip the data URL prefix to get raw base64
      const base64 = preview.dataUrl.split(',')[1];
      await api.uploadSignatureImage(userObjKey, base64, preview.mimeType);
      pushSuccess('Signature uploaded');
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['signatureImage', userObjKey] });
    } catch (e) {
      pushError(`Upload failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    setUploading(false);
  }, [preview, userObjKey, qc]);

  const handleRemove = useCallback(async () => {
    if (!userObjKey) return;
    try {
      await api.removeSignatureImage(userObjKey);
      pushSuccess('Signature removed');
      qc.invalidateQueries({ queryKey: ['signatureImage', userObjKey] });
    } catch (e) {
      pushError(`Remove failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [userObjKey, qc]);

  if (!userObjKey) {
    return <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>Save the user first to manage the signature image.</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Upload a signature image used in reports (PNG, JPG, GIF). Max ~500 KB recommended.</Typography>

      {/* Current signature */}
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>Current Signature</Typography>
        {loadingSig ? (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Loading…</Typography>
        ) : currentDataUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box component="img" src={currentDataUrl} alt="Signature"
              sx={{ maxWidth: 300, maxHeight: 120, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5, bgcolor: '#fff' }} />
            <Box>
              <Button size="small" color="error" variant="outlined" onClick={handleRemove} sx={{ fontSize: 11, py: 0.25 }}>
                Remove Signature
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>No signature on file.</Typography>
        )}
      </Box>

      <Divider />

      {/* Upload new */}
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>Upload New Signature</Typography>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
            onClick={() => fileRef.current?.click()} sx={{ fontSize: 11, py: 0.25 }}>
            Choose File
          </Button>
          {preview && (
            <Button size="small" variant="contained" onClick={handleUpload} disabled={uploading} sx={{ fontSize: 11, py: 0.25 }}>
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          )}
        </Box>
        {preview && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>Preview:</Typography>
            <Box component="img" src={preview.dataUrl} alt="Preview"
              sx={{ maxWidth: 300, maxHeight: 120, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5, bgcolor: '#fff' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * UserDialog — tabbed editor: General, Contact, DICOM, Address
 * ══════════════════════════════════════════════════════════════════════════ */
interface UserDialogProps {
  user: User | null; // null = add mode
  cloneFrom?: User | null; // pre-fill from this user in add mode
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const ROLES = [
  { value: 'ADMINISTRATOR', label: 'Administrator' },
  { value: 'RADIOLOGIST', label: 'Radiologist' },
  { value: 'RESIDENT', label: 'Resident' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'TRANSCRIPTIONIST', label: 'Transcriptionist' },
  { value: 'PHYSICIAN', label: 'Physician' },
  { value: 'TECH', label: 'Technologist' },
  { value: 'CLERK', label: 'Clerk' },
  { value: 'PATIENT', label: 'Patient' },
  { value: 'NURSE', label: 'Nurse' },
  { value: 'MODALITY', label: 'Modality' },
  { value: 'LOCATION', label: 'Location' },
];

const PHYSICIAN_SUBTYPES = [
  { value: '', label: '— None —' },
  { value: 'General Radiologist', label: 'General Radiologist' },
  { value: 'Neuroradiologist', label: 'Neuroradiologist' },
  { value: 'Musculoskeletal', label: 'Musculoskeletal' },
  { value: 'Interventional', label: 'Interventional' },
  { value: 'Pediatric', label: 'Pediatric' },
  { value: 'Breast Imaging', label: 'Breast Imaging' },
  { value: 'Nuclear Medicine', label: 'Nuclear Medicine' },
  { value: 'Body Imaging', label: 'Body Imaging' },
  { value: 'Cardiothoracic', label: 'Cardiothoracic' },
  { value: 'Emergency', label: 'Emergency' },
  { value: 'Cardiologist', label: 'Cardiologist' },
];

/** Map user type → ACL role suffix (matches User.Role.getSuffix() in Java) */
const ROLE_SUFFIX: Record<string, string> = {
  ADMINISTRATOR: 'admin', RADIOLOGIST: 'rad', RESIDENT: 'res',
  MANAGER: 'mgr', TRANSCRIPTIONIST: 'trans', PHYSICIAN: 'phys',
  TECH: 'tech', CLERK: 'clerk', PATIENT: 'patient', NURSE: 'nurse',
  MODALITY: 'modality', LOCATION: 'location', COORDINATOR: 'coord',
  DIRECTOR: 'dir', ORDERENTRY: 'order', BILLINGCLERK: 'bill',
};

const UserDialog = ({ user, cloneFrom, open, onClose, onSave }: UserDialogProps) => {
  // Clone mode: treat as "add" but pre-fill from cloneFrom
  const sourceUser = user ?? cloneFrom ?? null;
  const isEdit = !!user;
  const isClone = !user && !!cloneFrom;
  const raw = sourceUser as Record<string, unknown> | null;
  const s = useMemo(() => (sourceUser ? sched(sourceUser) : {}), [sourceUser]);
  const ci = (s.contactInfo ?? s) as Record<string, unknown>;
  const ai = (s.addressInfo ?? s) as Record<string, unknown>;
  const alertInfo = (s.alertInfo ?? s) as Record<string, unknown>;
  const avBlob = (s.availability ?? {}) as Record<string, { available?: boolean; start?: string; end?: string }>;
  const detBlob = useMemo(() => (sourceUser ? det(sourceUser) : {}), [sourceUser]);
  const { user: sessionUser } = useAuth();
  const sessionIsRoot = !!sessionUser?.acl?.includes('role:root-admin');

  const [tab, setTab] = useState(0);

  // Availability schedule state — default to Mon–Fri 08:00–17:00
  const [availability, setAvailability] = useState<Record<string, { available: boolean; start: string; end: string }>>(() => {
    const defaults: Record<string, { available: boolean; start: string; end: string }> = {};
    ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].forEach((day) => {
      const prev = avBlob[day];
      defaults[day] = { available: prev?.available ?? (day !== 'SATURDAY' && day !== 'SUNDAY'), start: prev?.start ?? '08:00', end: prev?.end ?? '17:00' };
    });
    return defaults;
  });

  const setAvDay = (day: string, field: 'available' | 'start' | 'end', val: boolean | string) =>
    setAvailability((prev) => ({ ...prev, [day]: { ...prev[day], [field]: val } }));

  // ── Identity ──
  const [userId, setUserId] = useState(isClone ? '' : (raw?.userId ?? sourceUser?.user ?? '') as string);
  const [siteId, setSiteId] = useState((raw?.siteid ?? sourceUser?.siteId ?? '') as string);
  const [userType, setUserType] = useState(((raw?.userType ?? sourceUser?.role ?? 'PHYSICIAN') as string).toUpperCase());
  const [userSubType, setUserSubType] = useState((raw?.userSubType ?? '') as string);

  // ── Name (DICOM PN: Last^First^Middle^Prefix^Suffix) ──
  const parsedName = useMemo(() => {
    const nameStr = (raw?.userName ?? raw?.niceName ?? sourceUser?.name ?? '') as string;
    // If the name contains '^', it's DICOM PN format; otherwise try niceName as-is
    if (nameStr.includes('^')) return parseDicomPN(nameStr);
    // Single string: treat as display name — put in last name for now
    return { last: nameStr, first: '', middle: '', prefix: '', suffix: '' };
  }, [raw, sourceUser]);
  const [lastName, setLastName] = useState(isClone ? `${parsedName.last} (copy)` : parsedName.last);
  const [firstName, setFirstName] = useState(parsedName.first);
  const [middleName, setMiddleName] = useState(parsedName.middle);
  const [namePrefix, setNamePrefix] = useState(parsedName.prefix);
  const [nameSuffix, setNameSuffix] = useState(parsedName.suffix);

  // ── Security ──
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const origPwNeverExpires = useMemo(() => (raw?.pwNeverExpires ?? false) as boolean, [raw]);
  const [pwNeverExpires, setPwNeverExpires] = useState(origPwNeverExpires);
  const [tempPassword, setTempPassword] = useState(false);

  // ── Contact ──
  const [officePhone, setOfficePhone] = useState((ci.office ?? '') as string);
  const [mobilePhone, setMobilePhone] = useState((ci.mobile ?? '') as string);
  const [homePhone, setHomePhone] = useState((ci.home ?? '') as string);
  const [faxNumber, setFaxNumber] = useState((ci.fax ?? '') as string);
  const [pagerNumber, setPagerNumber] = useState((ci.pager ?? '') as string);
  const [emailAddress, setEmailAddress] = useState((ci.email ?? '') as string);
  const [npi, setNpi] = useState((raw?.npi ?? (detBlob.NPI ?? detBlob.npi ?? '')) as string);

  // ── Alert Preferences ──
  const [prefPriority1, setPrefPriority1] = useState((alertInfo.prefPriority1 ?? '') as string);
  const [prefPriority2, setPrefPriority2] = useState((alertInfo.prefPriority2 ?? '') as string);
  const [locationObjKey, setLocationObjKey] = useState((alertInfo.location ?? '') as string);
  const [backupContactObjKey, setBackupContactObjKey] = useState((alertInfo.backup ?? '') as string);

  // ── Address ──
  const [addressLine, setAddressLine] = useState((ai.addressLine ?? '') as string);
  const [city, setCity] = useState((ai.city ?? '') as string);
  const [state, setState] = useState((ai.state ?? '') as string);
  const [zipCode, setZipCode] = useState((ai.zipCode ?? '') as string);

  // ── Locale ──
  const [language, setLanguage] = useState((detBlob.language ?? '') as string);

  // ── DICOM ──
  const [dicomAe, setDicomAe] = useState(isClone ? '' : (sourceUser?.dicomAe ?? ''));
  const [dicomHost, setDicomHost] = useState(isClone ? '' : (sourceUser?.dicomHost ?? ''));
  const [dicomPort, setDicomPort] = useState(isClone ? '' : String(sourceUser?.dicomPort ?? ''));
  const [secureDicom, setSecureDicom] = useState((s.secureDicom ?? false) as boolean);
  const [includeFilesAsDicom, setIncludeFilesAsDicom] = useState((s.includeFilesAsDicom ?? false) as boolean);
  const [convertPDFToJPG, setConvertPDFToJPG] = useState((s.convertPDFFilesToJPG ?? false) as boolean);
  const [includeReportsAsDicom, setIncludeReportsAsDicom] = useState((s.includeRadReportsAsDicom ?? false) as boolean);
  const [dontSendPrelimReports, setDontSendPrelimReports] = useState((s.dontSendPrelimReportsAsDicom ?? false) as boolean);
  const [sopClassMode, setSopClassMode] = useState<'allow' | 'exclude'>((s.allowImageSopClasses ?? true) as boolean ? 'allow' : 'exclude');
  const [imageSopClasses, setImageSopClasses] = useState(() => {
    const arr = (s.imageSopClasses ?? []) as string[];
    return Array.isArray(arr) ? arr.join('\n') : '';
  });
  const [modalityTypeOverride, setModalityTypeOverride] = useState((detBlob.modalityTypeOverride ?? '') as string);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [showSendFilters, setShowSendFilters] = useState(false);

  // ── ACL state (Site Access / Access Groups / Custom Roles) ──
  const initialAcl = useMemo(() => new Set(sourceUser?.acl ?? []), [sourceUser]);

  // Site Access: track per-site checkboxes — { siteId: { role: bool, list: bool } }
  const [siteChecks, setSiteChecks] = useState<Record<string, { role: boolean; list: boolean }>>(() => {
    const checks: Record<string, { role: boolean; list: boolean }> = {};
    const acl = sourceUser?.acl ?? [];
    const siteIds = new Set<string>();
    for (const entry of acl) {
      if (entry.startsWith('role:') && entry.includes('-')) {
        siteIds.add(entry.substring(5, entry.indexOf('-')));
      }
    }
    for (const sid of siteIds) {
      const suffix = ROLE_SUFFIX[userType] ?? 'phys';
      checks[sid] = {
        role: acl.includes(`role:${sid}-${suffix}`),
        list: acl.includes(`role:${sid}-list`),
      };
    }
    return checks;
  });

  // Access Groups: set of assigned group objKeys
  const [assignedGroups, setAssignedGroups] = useState<Set<string>>(() => {
    const acl = sourceUser?.acl ?? [];
    return new Set(acl.filter((e) => e.startsWith('group:')));
  });

  // Custom Roles: set of assigned custom role objKeys
  const [assignedCustomRoles, setAssignedCustomRoles] = useState<Set<string>>(() => {
    const acl = sourceUser?.acl ?? [];
    return new Set(acl.filter((e) => e.startsWith('crole:')));
  });

  // Queries for ACL tabs
  const { data: groupsData } = useQuery({
    queryKey: ['allGroups'],
    queryFn: () => api.getGroups(),
    staleTime: 60_000,
  });
  const { data: customRolesData } = useQuery({
    queryKey: ['allCustomRoles'],
    queryFn: () => api.getCustomRoles(),
    staleTime: 60_000,
  });

  // Queries for Saved Searches tab (T9)
  const { data: savedSearchesData, refetch: refetchSearches } = useQuery({
    queryKey: ['savedSearches', sourceUser?.objKey],
    queryFn: () => api.getSavedSearches(sourceUser?.objKey, true),
    enabled: isEdit && !!sourceUser?.objKey,
    staleTime: 30_000,
  });

  // Queries for Viewer Layout tab (T10)
  const { data: userConfigData } = useQuery({
    queryKey: ['userConfig', sourceUser?.objKey],
    queryFn: () => api.getUserConfig(sourceUser!.objKey),
    enabled: isEdit && !!sourceUser?.objKey,
    staleTime: 30_000,
  });

  // ── Viewer Layout state (T10) ──
  const [viewerConfigApplied, setViewerConfigApplied] = useState(false);
  const [viewerCustomize, setViewerCustomize] = useState(false);
  const [viewerPrefs, setViewerPrefs] = useState<Set<string>>(new Set());
  const [pdfBkColor, setPdfBkColor] = useState('FFFFFF');
  const [enableStudyViewerLog, setEnableStudyViewerLog] = useState(false);
  const [customizeDelay, setCustomizeDelay] = useState(false);
  const [strokeDelay, setStrokeDelay] = useState('15');
  const [statDelay, setStatDelay] = useState('30');
  const [otherDelay, setOtherDelay] = useState('60');

  // Apply viewer config from query once available (runs during render, sets flag to avoid re-running)
  if (userConfigData && !viewerConfigApplied) {
    setViewerConfigApplied(true);
    const cfg = userConfigData;
    const vp = cfg.viewerPreferences as string[] | null | undefined;
    if (vp && Array.isArray(vp)) {
      setViewerCustomize(true);
      setViewerPrefs(new Set(vp));
    }
    const pbk = cfg.pdfBkColor as string | undefined;
    if (pbk) setPdfBkColor(pbk);
    if (cfg.enableStudyViewerLog) setEnableStudyViewerLog(true);
    const delay = cfg.delay as Record<string, string> | undefined;
    if (delay) {
      setCustomizeDelay(true);
      if (delay.strokeDelay) setStrokeDelay(String(Math.round(Number(delay.strokeDelay) / 1000)));
      if (delay.statDelay) setStatDelay(String(Math.round(Number(delay.statDelay) / 1000)));
      if (delay.otherDelay) setOtherDelay(String(Math.round(Number(delay.otherDelay) / 1000)));
    }
  }

  const [saving, setSaving] = useState(false);

  // ── User suggestions for Location / Backup Contact ──
  const [locationSearch, setLocationSearch] = useState('');
  const [backupSearch, setBackupSearch] = useState('');

  const { data: locationSuggestions } = useQuery({
    queryKey: ['userSuggestions', 'location', locationSearch],
    queryFn: () => api.getUserSuggestions({ query: locationSearch, limit: 20, role: 'LOCATION' }),
    enabled: locationSearch.length >= 2,
    staleTime: 10_000,
  });

  const { data: backupSuggestions } = useQuery({
    queryKey: ['userSuggestions', 'backup', backupSearch],
    queryFn: () => api.getUserSuggestions({ query: backupSearch, limit: 20 }),
    enabled: backupSearch.length >= 2,
    staleTime: 10_000,
  });

  // Site picker
  const { data: sitesData } = useQuery({
    queryKey: queryKeys.sites.all,
    queryFn: () => api.getSites(),
    staleTime: 60_000,
  });
  const siteOptions = (sitesData?.records ?? []).map((s) => {
    const r = (s as unknown) as Record<string, unknown>;
    const id = ((r.siteId ?? r.id ?? '') as string);
    return { label: [id, s.name].filter(Boolean).join(' — '), value: id };
  });

  // ── Derived ──
  const isPhysicianType = ['RADIOLOGIST', 'PHYSICIAN', 'RESIDENT', 'NURSE'].includes(userType);
  const isModality = userType === 'MODALITY';
  const isLocation = userType === 'LOCATION';
  const isDeviceType = isModality || isLocation;
  const needsNPI = isPhysicianType;
  const needsSubType = userType === 'RADIOLOGIST' || userType === 'DIRECTOR';

  const handleSave = useCallback(async () => {
    // Validate password confirmation
    if (password && password !== passwordConfirm) {
      pushError('Password and confirmation do not match');
      return;
    }

    setSaving(true);
    try {
      const dicomPNName = buildDicomPN(lastName, firstName, middleName, namePrefix, nameSuffix);

      // Build schedule JSON (merge with existing to avoid losing unknown fields)
      const scheduleObj: Record<string, unknown> = { ...s };
      scheduleObj.contactInfo = { office: officePhone, mobile: mobilePhone, home: homePhone, fax: faxNumber, pager: pagerNumber, email: emailAddress };
      scheduleObj.addressInfo = { addressLine, city, state, zipCode };
      scheduleObj.alertInfo = { location: locationObjKey, prefPriority1, prefPriority2, backup: backupContactObjKey };
      scheduleObj.availability = availability;
      scheduleObj.secureDicom = secureDicom;
      scheduleObj.includeFilesAsDicom = includeFilesAsDicom;
      scheduleObj.convertPDFFilesToJPG = convertPDFToJPG;
      scheduleObj.includeRadReportsAsDicom = includeReportsAsDicom;
      scheduleObj.dontSendPrelimReportsAsDicom = dontSendPrelimReports;
      scheduleObj.allowImageSopClasses = sopClassMode === 'allow';
      scheduleObj.imageSopClasses = imageSopClasses.split('\n').map((s) => s.trim()).filter(Boolean);

      // Build details JSON (merge with existing)
      const detailsObj: Record<string, unknown> = { ...detBlob };
      if (npi) detailsObj.NPI = npi;
      if (language) detailsObj.language = language;
      if (modalityTypeOverride) detailsObj.modalityTypeOverride = modalityTypeOverride;

      if (!isEdit) {
        // ── CREATE MODE ──
        const createResult = await api.addModifyUser({
          siteId,
          userId,
          userName: dicomPNName || niceNameFromPN(dicomPNName) || userId,
          userType,
          autoCreate: true,
          ...(password ? { password } : {}),
          ...(tempPassword ? { tempPw: true } : {}),
        });

        // Get objKey from response for follow-up saves
        const newObjKey = typeof createResult === 'object' && createResult !== null
          ? (createResult as Record<string, unknown>).objKey as string | undefined
          : undefined;

        if (newObjKey) {
          // Save full schedule/details/dicom via saveUserAndConfig
          await api.saveUserAndConfig({
            user: {
              objKey: newObjKey,
              userName: dicomPNName,
              dicomAe: dicomAe || undefined,
              dicomHost: dicomHost || undefined,
              dicomPort: dicomPort ? Number(dicomPort) : undefined,
              schedule: JSON.stringify(scheduleObj),
              details: JSON.stringify(detailsObj),
            },
          });

          if (pwNeverExpires) {
            await api.changePasswordNeverExpires(newObjKey, true);
          }
        }
      } else {
        // ── EDIT MODE ──
        await api.saveUserAndConfig({
          user: {
            objKey: user!.objKey,
            userName: dicomPNName,
            dicomAe: dicomAe || undefined,
            dicomHost: dicomHost || undefined,
            dicomPort: dicomPort ? Number(dicomPort) : undefined,
            schedule: JSON.stringify(scheduleObj),
            details: JSON.stringify(detailsObj),
          },
        });

        // Password change via addModifyUser (handles changePassword correctly)
        if (password) {
          await api.addModifyUser({
            siteId: siteId || (raw?.siteid as string) || '',
            userId,
            password,
            ...(tempPassword ? { tempPw: true } : {}),
          });
        }

        // Password never expires toggle
        if (pwNeverExpires !== origPwNeverExpires) {
          await api.changePasswordNeverExpires(user!.objKey, pwNeverExpires);
        }

        // Viewer Layout config save (T10)
        if (viewerCustomize || enableStudyViewerLog) {
          const configObj: Record<string, unknown> = { ...(userConfigData ?? {}) };
          configObj.enableStudyViewerLog = enableStudyViewerLog;
          if (viewerCustomize) {
            configObj.viewerPreferences = [...viewerPrefs];
            configObj.pdfBkColor = pdfBkColor || null;
            configObj.siteConfig = false;
            const isRadRes = userType === 'RADIOLOGIST' || userType === 'RESIDENT';
            if (isRadRes) configObj.deliveryOption = true;
            if (customizeDelay && isRadRes) {
              configObj.delay = {
                strokeDelay: String(Number(strokeDelay) * 1000),
                statDelay: String(Number(statDelay) * 1000),
                otherDelay: String(Number(otherDelay) * 1000),
              };
            } else {
              configObj.delay = null;
            }
          } else {
            configObj.viewerPreferences = null;
            configObj.siteConfig = true;
            configObj.delay = null;
            configObj.pdfBkColor = null;
          }
          await api.saveUserAndConfig({ userKey: user!.objKey, config: JSON.stringify(configObj) });
        }

        // ACL changes (Site Access + Access Groups + Custom Roles)
        const aclAdd: string[] = [];
        const aclRemove: string[] = [];
        // Site Access changes
        const suffix = ROLE_SUFFIX[userType] ?? 'phys';
        for (const [sid, checks] of Object.entries(siteChecks)) {
          const roleEntry = `role:${sid}-${suffix}`;
          const listEntry = `role:${sid}-list`;
          if (checks.role && !initialAcl.has(roleEntry)) aclAdd.push(roleEntry);
          if (!checks.role && initialAcl.has(roleEntry)) aclRemove.push(roleEntry);
          if (checks.list && !initialAcl.has(listEntry)) aclAdd.push(listEntry);
          if (!checks.list && initialAcl.has(listEntry)) aclRemove.push(listEntry);
        }
        // Access Group changes
        for (const gk of assignedGroups) {
          if (!initialAcl.has(gk)) aclAdd.push(gk);
        }
        for (const gk of initialAcl) {
          if (gk.startsWith('group:') && !assignedGroups.has(gk)) aclRemove.push(gk);
        }
        // Custom Role changes
        for (const rk of assignedCustomRoles) {
          if (!initialAcl.has(rk)) aclAdd.push(rk);
        }
        for (const rk of initialAcl) {
          if (rk.startsWith('crole:') && !assignedCustomRoles.has(rk)) aclRemove.push(rk);
        }
        if (aclAdd.length > 0 || aclRemove.length > 0) {
          await api.updateUserAcl(user!.objKey, aclAdd, aclRemove);
        }
      }

      pushSuccess(`User ${isEdit ? 'updated' : isClone ? 'cloned' : 'created'} successfully`);
      onSave();
    } catch (e) {
      pushError(`Failed to save user: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaving(false);
  }, [userId, siteId, lastName, firstName, middleName, namePrefix, nameSuffix, userType,
      password, passwordConfirm, tempPassword, pwNeverExpires, origPwNeverExpires,
      isEdit, isClone, onSave, user, raw, s, detBlob,
      officePhone, mobilePhone, homePhone, faxNumber, pagerNumber, emailAddress, npi,
      prefPriority1, prefPriority2, locationObjKey, backupContactObjKey,
      dicomAe, dicomHost, dicomPort, secureDicom,
      includeFilesAsDicom, convertPDFToJPG, includeReportsAsDicom, dontSendPrelimReports,
      sopClassMode, imageSopClasses, modalityTypeOverride,
      addressLine, city, state, zipCode, language, availability,
      siteChecks, assignedGroups, assignedCustomRoles, initialAcl,
      viewerCustomize, viewerPrefs, pdfBkColor, enableStudyViewerLog,
      customizeDelay, strokeDelay, statDelay, otherDelay, userConfigData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: 780, maxHeight: '90vh', borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        {isEdit ? `Edit User — ${userId}` : isClone ? `Clone User — from ${cloneFrom?.user ?? ''}` : 'Add User'}
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 2, minHeight: 36, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label="Profile" sx={tabSx} />
        <Tab label="DICOM" sx={tabSx} />
        <Tab label="Permissions" sx={tabSx} />
        <Tab label="Schedule" sx={tabSx} />
        <Tab label="Signature" sx={tabSx} />
        <Tab label="Site Access" sx={tabSx} />
        <Tab label="Access Groups" sx={tabSx} />
        <Tab label="Custom Roles" sx={tabSx} />
        <Tab label="Saved Searches" sx={tabSx} />
        <Tab label="Viewer Layout" sx={tabSx} />
        <Tab label="Monitor Config" sx={tabSx} />
      </Tabs>

      <DialogContent sx={{ py: 2, minHeight: 380, maxHeight: '65vh', overflowY: 'auto' }}>
        {/* ══════════════════════ Profile Tab ══════════════════════ */}
        {tab === 0 && <>
          {/* ── Identity ── */}
          <SectionLabel>Identity</SectionLabel>
          <FR l="User ID *"><TextField size="small" fullWidth value={userId} onChange={(e) => setUserId(e.target.value)} disabled={isEdit} inputProps={{ maxLength: 16 }} sx={fsx} /></FR>
          <FR l="Site *">
            <Autocomplete
              size="small"
              options={siteOptions}
              value={siteOptions.find((o) => o.value === siteId) ?? (siteId ? { label: siteId, value: siteId } : null)}
              onChange={(_e, opt) => setSiteId(opt?.value ?? '')}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              disabled={isEdit}
              renderInput={(params) => <TextField {...params} required placeholder="Select site…" sx={fsx} />}
              sx={fsx}
            />
          </FR>
          <FR l="Role *">
            <TextField size="small" fullWidth select value={userType} onChange={(e) => setUserType(e.target.value)} sx={fsx}>
              {ROLES.map((r) => <MenuItem key={r.value} value={r.value} sx={{ fontSize: 12 }}>{r.label}</MenuItem>)}
            </TextField>
          </FR>
          {needsSubType && (
            <FR l="Physician Type">
              <TextField size="small" fullWidth select value={userSubType} onChange={(e) => setUserSubType(e.target.value)} sx={fsx}>
                {PHYSICIAN_SUBTYPES.map((r) => <MenuItem key={r.value} value={r.value} sx={{ fontSize: 12 }}>{r.label}</MenuItem>)}
              </TextField>
            </FR>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* ── Name ── */}
          <SectionLabel>Name</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            <FR l="First Name"><TextField size="small" fullWidth value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={fsx} /></FR>
            <FR l="Middle Name"><TextField size="small" fullWidth value={middleName} onChange={(e) => setMiddleName(e.target.value)} sx={fsx} /></FR>
          </Box>
          <FR l="Last Name *"><TextField size="small" fullWidth value={lastName} onChange={(e) => setLastName(e.target.value)} sx={fsx} /></FR>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            <FR l="Prefix"><TextField size="small" fullWidth value={namePrefix} onChange={(e) => setNamePrefix(e.target.value)} sx={fsx} placeholder="e.g. Dr." /></FR>
            <FR l="Suffix"><TextField size="small" fullWidth value={nameSuffix} onChange={(e) => setNameSuffix(e.target.value)} sx={fsx} placeholder="e.g. MD" /></FR>
          </Box>
          {needsNPI && (
            <FR l="NPI"><TextField size="small" fullWidth value={npi} onChange={(e) => setNpi(e.target.value)} sx={fsx} placeholder="National Provider Identifier" inputProps={{ maxLength: 16 }} /></FR>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* ── Security ── */}
          {!isDeviceType && <>
            <SectionLabel>Security</SectionLabel>
            <FR l={isEdit ? 'New Password' : 'Password *'}>
              <TextField size="small" fullWidth type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? '(leave blank to keep)' : ''} sx={fsx} />
            </FR>
            <FR l="Confirm Password">
              <TextField size="small" fullWidth type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)}
                error={!!password && !!passwordConfirm && password !== passwordConfirm}
                helperText={password && passwordConfirm && password !== passwordConfirm ? 'Passwords do not match' : undefined}
                sx={fsx} />
            </FR>
            <Box sx={{ display: 'flex', gap: 2, ml: '118px' }}>
              {sessionIsRoot && (
                <FormControlLabel
                  control={<Checkbox size="small" checked={pwNeverExpires} onChange={(e) => setPwNeverExpires(e.target.checked)} sx={{ p: 0.25 }} />}
                  label={<Typography sx={{ fontSize: 11 }}>Password Never Expires</Typography>}
                />
              )}
              <FormControlLabel
                control={<Checkbox size="small" checked={tempPassword} onChange={(e) => setTempPassword(e.target.checked)} sx={{ p: 0.25 }} />}
                label={<Typography sx={{ fontSize: 11 }}>Make Temporary Password</Typography>}
              />
            </Box>

            <Divider sx={{ my: 1.5 }} />
          </>}

          {/* ── Contact ── */}
          <SectionLabel>Contact Information</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            <FR l="Mobile Phone"><TextField size="small" fullWidth value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} sx={fsx} /></FR>
            <FR l="Office Phone"><TextField size="small" fullWidth value={officePhone} onChange={(e) => setOfficePhone(e.target.value)} sx={fsx} /></FR>
            <FR l="Home Phone"><TextField size="small" fullWidth value={homePhone} onChange={(e) => setHomePhone(e.target.value)} sx={fsx} /></FR>
            <FR l="Pager"><TextField size="small" fullWidth value={pagerNumber} onChange={(e) => setPagerNumber(e.target.value)} sx={fsx} /></FR>
            <FR l="Fax"><TextField size="small" fullWidth value={faxNumber} onChange={(e) => setFaxNumber(e.target.value)} sx={fsx} /></FR>
            <FR l="Email"><TextField size="small" fullWidth value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} sx={fsx} /></FR>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* ── Alert Preferences ── */}
          <SectionLabel>Alert Preferences</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            <FR l="Priority Contact 1">
              <TextField size="small" fullWidth select value={prefPriority1} onChange={(e) => setPrefPriority1(e.target.value)} sx={fsx}>
                {CONTACT_METHODS.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 12 }}>{m.label}</MenuItem>)}
              </TextField>
            </FR>
            <FR l="Priority Contact 2">
              <TextField size="small" fullWidth select value={prefPriority2} onChange={(e) => setPrefPriority2(e.target.value)} sx={fsx}>
                {CONTACT_METHODS.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 12 }}>{m.label}</MenuItem>)}
              </TextField>
            </FR>
          </Box>
          <FR l="Location">
            <Autocomplete
              size="small" freeSolo
              options={locationSuggestions ?? []}
              getOptionLabel={(o) => typeof o === 'string' ? o : o.displayString}
              inputValue={locationSearch || locationObjKey}
              onInputChange={(_e, v) => setLocationSearch(v)}
              onChange={(_e, v) => {
                if (v && typeof v !== 'string') setLocationObjKey(v.objKey);
                else setLocationObjKey(typeof v === 'string' ? v : '');
              }}
              renderInput={(params) => <TextField {...params} placeholder="Search locations…" sx={fsx} />}
              sx={fsx}
            />
          </FR>
          <FR l="Backup Contact">
            <Autocomplete
              size="small" freeSolo
              options={backupSuggestions ?? []}
              getOptionLabel={(o) => typeof o === 'string' ? o : o.displayString}
              inputValue={backupSearch || backupContactObjKey}
              onInputChange={(_e, v) => setBackupSearch(v)}
              onChange={(_e, v) => {
                if (v && typeof v !== 'string') setBackupContactObjKey(v.objKey);
                else setBackupContactObjKey(typeof v === 'string' ? v : '');
              }}
              renderInput={(params) => <TextField {...params} placeholder="Search users…" sx={fsx} />}
              sx={fsx}
            />
          </FR>

          <Divider sx={{ my: 1.5 }} />

          {/* ── Address ── */}
          <SectionLabel>Address</SectionLabel>
          <FR l="Address"><TextField size="small" fullWidth value={addressLine} onChange={(e) => setAddressLine(e.target.value)} sx={fsx} multiline minRows={2} /></FR>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 0.75 }}>
            <FR l="City"><TextField size="small" fullWidth value={city} onChange={(e) => setCity(e.target.value)} sx={fsx} /></FR>
            <FR l="State"><TextField size="small" fullWidth value={state} onChange={(e) => setState(e.target.value)} sx={fsx} inputProps={{ maxLength: 2 }} /></FR>
            <FR l="Zip"><TextField size="small" fullWidth value={zipCode} onChange={(e) => setZipCode(e.target.value)} sx={fsx} /></FR>
          </Box>

          {/* ── Language ── */}
          <FR l="Language">
            <TextField size="small" fullWidth select value={language} onChange={(e) => setLanguage(e.target.value)} sx={fsx}>
              {LANGUAGES.map((l) => <MenuItem key={l.value} value={l.value} sx={{ fontSize: 12 }}>{l.label}</MenuItem>)}
            </TextField>
          </FR>
        </>}

        {/* ══════════════════════ DICOM Tab ══════════════════════ */}
        {tab === 1 && <>
          <FR l="AE Title"><TextField size="small" fullWidth value={dicomAe} onChange={(e) => setDicomAe(e.target.value)} sx={fsx} inputProps={{ maxLength: 16 }} /></FR>
          <FR l="Host"><TextField size="small" fullWidth value={dicomHost} onChange={(e) => setDicomHost(e.target.value)} sx={fsx} placeholder='Use "auto" for auto-detect' /></FR>
          <FR l="Port"><TextField size="small" fullWidth type="number" value={dicomPort} onChange={(e) => setDicomPort(e.target.value)} sx={fsx} inputProps={{ max: 65535 }} /></FR>

          {isModality && (
            <FR l="Modality Override"><TextField size="small" fullWidth value={modalityTypeOverride} onChange={(e) => setModalityTypeOverride(e.target.value)} sx={fsx} placeholder="e.g. CR, DX, MR" /></FR>
          )}

          <Divider sx={{ my: 1.5 }} />

          <FormControl component="fieldset" sx={{ mb: 1.5 }}>
            <FormLabel sx={{ fontSize: 11, fontWeight: 600 }}>DICOM TLS</FormLabel>
            <RadioGroup row value={secureDicom ? 'yes' : 'no'} onChange={(e) => setSecureDicom(e.target.value === 'yes')}>
              <FormControlLabel value="yes" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Yes</Typography>} />
              <FormControlLabel value="no" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>No</Typography>} />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 1 }} />

          {/* ── Send Options ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 0.5 }} onClick={() => setShowSendOptions(!showSendOptions)}>
            <ExpandMoreIcon sx={{ fontSize: 16, transform: showSendOptions ? 'rotate(0deg)' : 'rotate(-90deg)', transition: '0.2s', mr: 0.5 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Send Options</Typography>
          </Box>
          <Collapse in={showSendOptions}>
            <Box sx={{ pl: 2, mb: 1 }}>
              <FormControlLabel control={<Checkbox size="small" checked={includeFilesAsDicom} onChange={(e) => setIncludeFilesAsDicom(e.target.checked)} sx={{ p: 0.25 }} />}
                label={<Typography sx={{ fontSize: 11 }}>Include attached files as DICOM objects</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={convertPDFToJPG} onChange={(e) => setConvertPDFToJPG(e.target.checked)} sx={{ p: 0.25 }} />}
                label={<Typography sx={{ fontSize: 11 }}>Convert PDF files to secondary captures</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={includeReportsAsDicom} onChange={(e) => setIncludeReportsAsDicom(e.target.checked)} sx={{ p: 0.25 }} />}
                label={<Typography sx={{ fontSize: 11 }}>Include radiologist reports as DICOM objects</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={dontSendPrelimReports} onChange={(e) => setDontSendPrelimReports(e.target.checked)} sx={{ p: 0.25 }} />}
                label={<Typography sx={{ fontSize: 11 }}>{"Don't send preliminary reports"}</Typography>} />
            </Box>
          </Collapse>

          {/* ── Send Filters ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 0.5 }} onClick={() => setShowSendFilters(!showSendFilters)}>
            <ExpandMoreIcon sx={{ fontSize: 16, transform: showSendFilters ? 'rotate(0deg)' : 'rotate(-90deg)', transition: '0.2s', mr: 0.5 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Send Filter — Image SOP Classes</Typography>
          </Box>
          <Collapse in={showSendFilters}>
            <Box sx={{ pl: 2, mb: 1 }}>
              <FormControl component="fieldset" sx={{ mb: 1 }}>
                <RadioGroup row value={sopClassMode} onChange={(e) => setSopClassMode(e.target.value as 'allow' | 'exclude')}>
                  <FormControlLabel value="allow" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Allow only these SOP Classes</Typography>} />
                  <FormControlLabel value="exclude" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Exclude these SOP Classes</Typography>} />
                </RadioGroup>
              </FormControl>
              <TextField size="small" fullWidth multiline minRows={3} maxRows={8} value={imageSopClasses}
                onChange={(e) => setImageSopClasses(e.target.value)} placeholder="One SOP Class UID per line"
                sx={{ '& .MuiInputBase-root': { fontSize: 11, fontFamily: 'monospace' } }} />
            </Box>
          </Collapse>
        </>}

        {/* ══════════════════════ Permissions Tab ══════════════════════ */}
        {tab === 2 && <PermissionsTab user={user} sessionUser={sessionUser} userType={userType} />}

        {/* ══════════════════════ Schedule Tab ══════════════════════ */}
        {tab === 3 && <>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1.5 }}>{"Set this user\u2019s reading availability by day of week. Affects escalation and SLA calculations."}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '110px 80px 90px 90px', gap: 0.5, alignItems: 'center' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Day</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Available</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Start</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>End</Typography>
            {['MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY','SUNDAY'].map((day) => {
              const dv = availability[day] ?? { available: false, start: '08:00', end: '17:00' };
              return [
                <Typography key={`${day}-n`} sx={{ fontSize: 12 }}>{day.charAt(0) + day.slice(1).toLowerCase()}</Typography>,
                <Checkbox key={`${day}-cb`} size="small" checked={dv.available} onChange={(e) => setAvDay(day, 'available', e.target.checked)} sx={{ p: 0.25 }} />,
                <TextField key={`${day}-s`} size="small" value={dv.start} onChange={(e) => setAvDay(day, 'start', e.target.value)} disabled={!dv.available} placeholder="HH:MM" sx={{ '& .MuiInputBase-root': { fontSize: 11 } }} />,
                <TextField key={`${day}-e`} size="small" value={dv.end} onChange={(e) => setAvDay(day, 'end', e.target.value)} disabled={!dv.available} placeholder="HH:MM" sx={{ '& .MuiInputBase-root': { fontSize: 11 } }} />,
              ];
            })}
          </Box>
        </>}

        {/* ══════════════════════ Signature Tab ══════════════════════ */}
        {tab === 4 && <SignatureSection userObjKey={user?.objKey} />}

        {/* ══════════════════════ Site Access Tab ══════════════════════ */}
        {tab === 5 && (() => {
          const sites = (sitesData?.records ?? []).filter((si) => !si.status || si.status === 'active');
          const roleLabel = ROLES.find((r) => r.value === userType)?.label ?? userType;
          return (
            <Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
                {"Check sites where this user should have the system role or list access. Changes are saved when you click Save."}
              </Typography>
              {sites.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 2, textAlign: 'center' }}>No active sites found.</Typography>
              ) : (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px', gap: 0, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 0.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Site</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{roleLabel}</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>All Access</Typography>
                  </Box>
                  {sites.map((si) => {
                    const sid = si.siteId ?? ((si as unknown as Record<string, unknown>).id as string) ?? '';
                    const cur = siteChecks[sid] ?? { role: false, list: false };
                    const toggle = (field: 'role' | 'list') =>
                      setSiteChecks((prev) => ({ ...prev, [sid]: { ...cur, [field]: !cur[field] } }));
                    return (
                      <Box key={sid} sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px', gap: 0, px: 1, py: 0.25,
                        '&:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
                        '&:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven } }}>
                        <Typography sx={{ fontSize: 12, pt: 0.25 }}>{sid}{si.name ? ` \u2014 ${si.name}` : ''}</Typography>
                        <Checkbox size="small" checked={cur.role} onChange={() => toggle('role')} sx={{ p: 0.25 }} />
                        <Checkbox size="small" checked={cur.list} onChange={() => toggle('list')} sx={{ p: 0.25 }} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })()}

        {/* ══════════════════════ Access Groups Tab ══════════════════════ */}
        {tab === 6 && (() => {
          const allGroups = groupsData?.records ?? [];
          const available = allGroups.filter((g) => !assignedGroups.has(g.objKey));
          const assigned = allGroups.filter((g) => assignedGroups.has(g.objKey));
          return <ShuttlePanel
            title="Access Groups"
            availableLabel="Available Groups"
            assignedLabel="Current Groups"
            available={available.map((g) => ({ key: g.objKey, label: `${g.id} \u2014 ${g.name}`, sub: g.type }))}
            assigned={assigned.map((g) => ({ key: g.objKey, label: `${g.id} \u2014 ${g.name}`, sub: g.type }))}
            onAdd={(keys) => setAssignedGroups((prev) => { const s = new Set(prev); keys.forEach((k) => s.add(k)); return s; })}
            onRemove={(keys) => setAssignedGroups((prev) => { const s = new Set(prev); keys.forEach((k) => s.delete(k)); return s; })}
          />;
        })()}

        {/* ══════════════════════ Custom Roles Tab ══════════════════════ */}
        {tab === 7 && (() => {
          const allRoles = customRolesData?.records ?? [];
          const available = allRoles.filter((r) => !assignedCustomRoles.has(r.objKey));
          const assigned = allRoles.filter((r) => assignedCustomRoles.has(r.objKey));
          return <ShuttlePanel
            title="Custom Roles"
            availableLabel="Available Roles"
            assignedLabel="Current Roles"
            available={available.map((r) => ({ key: r.objKey, label: r.name || r.id, sub: r.siteName }))}
            assigned={assigned.map((r) => ({ key: r.objKey, label: r.name || r.id, sub: r.siteName }))}
            onAdd={(keys) => setAssignedCustomRoles((prev) => { const s = new Set(prev); keys.forEach((k) => s.add(k)); return s; })}
            onRemove={(keys) => setAssignedCustomRoles((prev) => { const s = new Set(prev); keys.forEach((k) => s.delete(k)); return s; })}
          />;
        })()}

        {/* ══════════════════════ Saved Searches Tab ══════════════════════ */}
        {tab === 8 && (() => {
          const searches: SavedSearch[] = savedSearchesData ?? [];
          const userSearches = searches.filter((s) => !s.isPredefined && !s.isSiteDefault);
          const siteSearches = searches.filter((s) => s.isPredefined || s.isSiteDefault);
          const handleDeleteSearch = async (searchId: string) => {
            try {
              await api.deleteSavedSearch(searchId, sourceUser?.objKey);
              pushSuccess('Saved search deleted');
              refetchSearches();
            } catch (e) {
              pushError(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
            }
          };
          const renderSearchList = (list: SavedSearch[], label: string, canDelete: boolean) => (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>{label} ({list.length})</Typography>
              {list.length === 0 ? (
                <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic', pl: 1 }}>None</Typography>
              ) : (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 90px 80px 50px', gap: 0, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 0.5 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600 }}>Name</Typography>
                    <Typography sx={{ fontSize: 10, fontWeight: 600 }}>Type</Typography>
                    <Typography sx={{ fontSize: 10, fontWeight: 600 }}>New Tab</Typography>
                    {canDelete && <Typography sx={{ fontSize: 10, fontWeight: 600 }}></Typography>}
                  </Box>
                  {list.map((s) => (
                    <Box key={s.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 90px 80px 50px', gap: 0, px: 1, py: 0.25,
                      '&:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
                      '&:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven } }}>
                      <Typography sx={{ fontSize: 11 }}>{s.searchName}{s.isDefault ? ' \u2605' : ''}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{s.savedSearchTypeName ?? (s.isSiteDefault ? 'Site' : 'User')}</Typography>
                      <Typography sx={{ fontSize: 11 }}>{s.openInANewTab ? 'Yes' : 'No'}</Typography>
                      {canDelete ? (
                        <IconButton size="small" onClick={() => handleDeleteSearch(s.id)} title="Delete" sx={{ width: 20, height: 20 }}>
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      ) : <Box />}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
          return (
            <Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1.5 }}>
                {'Manage saved searches for this user. Site-level searches (predefined) cannot be deleted here.'}
              </Typography>
              {!isEdit ? (
                <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 2, textAlign: 'center' }}>Save the user first to manage saved searches.</Typography>
              ) : (
                <>
                  {renderSearchList(userSearches, 'User Searches', true)}
                  {renderSearchList(siteSearches, 'Site / Predefined Searches', false)}
                </>
              )}
            </Box>
          );
        })()}

        {/* ══════════════════════ Viewer Layout Tab ══════════════════════ */}
        {tab === 9 && (() => {
          const isRadRes = userType === 'RADIOLOGIST' || userType === 'RESIDENT';
          const togglePref = (pref: string) => setViewerPrefs((prev) => {
            const s = new Set(prev);
            if (s.has(pref)) s.delete(pref); else s.add(pref);
            return s;
          });
          const deliveryVal = viewerPrefs.has('DELIVERY_DICOM') ? 'DICOM'
            : viewerPrefs.has('DELIVERY_PACS_VIEW') ? 'PACS_VIEW'
            : viewerPrefs.has('DELIVERY_OSIRIX') ? 'OSIRIX'
            : 'WORKS';
          const setDelivery = (val: string) => setViewerPrefs((prev) => {
            const s = new Set([...prev].filter((p) => !p.startsWith('DELIVERY_')));
            if (val === 'DICOM') s.add('DELIVERY_DICOM');
            else if (val === 'PACS_VIEW') s.add('DELIVERY_PACS_VIEW');
            else if (val === 'OSIRIX') s.add('DELIVERY_OSIRIX');
            else s.add('DELIVERY_WORKS');
            return s;
          });
          const iqVal = viewerPrefs.has('IMAGEQUALITY_HIGH') ? 'HIGH'
            : viewerPrefs.has('IMAGEQUALITY_PNG') ? 'PNG'
            : viewerPrefs.has('IMAGEQUALITY_GIF') ? 'GIF'
            : 'LOW';
          const setIQ = (val: string) => setViewerPrefs((prev) => {
            const s = new Set([...prev].filter((p) => !p.startsWith('IMAGEQUALITY_')));
            if (val === 'HIGH') s.add('IMAGEQUALITY_HIGH');
            else if (val === 'PNG') s.add('IMAGEQUALITY_PNG');
            else if (val === 'GIF') s.add('IMAGEQUALITY_GIF');
            else s.add('IMAGEQUALITY_LOW');
            return s;
          });
          // Study open behavior: unread
          const unreadBehavior = viewerPrefs.has('VIEWER_UNREAD_DELIV') ? 'VIEWER'
            : viewerPrefs.has('BRIT_DICTATE_UNREAD_DELIV') ? 'DICTATE'
            : 'REPORT';
          const setUnreadBehavior = (val: string) => setViewerPrefs((prev) => {
            const s = new Set([...prev].filter((p) => !['REPORT_EDITOR_UNREAD_DELIV','VIEWER_UNREAD_DELIV','BRIT_DICTATE_UNREAD_DELIV'].includes(p)));
            if (val === 'VIEWER') s.add('VIEWER_UNREAD_DELIV');
            else if (val === 'DICTATE') s.add('BRIT_DICTATE_UNREAD_DELIV');
            else s.add('REPORT_EDITOR_UNREAD_DELIV');
            return s;
          });
          // Study open behavior: signed
          const signedBehavior = viewerPrefs.has('VIEWER_READ_DELIV') ? 'VIEWER'
            : viewerPrefs.has('BRIT_DICTATE_READ_DELIV') ? 'DICTATE'
            : 'REPORT';
          const setSignedBehavior = (val: string) => setViewerPrefs((prev) => {
            const s = new Set([...prev].filter((p) => !['REPORT_EDITOR_READ_DELIV','VIEWER_READ_DELIV','BRIT_DICTATE_READ_DELIV'].includes(p)));
            if (val === 'VIEWER') s.add('VIEWER_READ_DELIV');
            else if (val === 'DICTATE') s.add('BRIT_DICTATE_READ_DELIV');
            else s.add('REPORT_EDITOR_READ_DELIV');
            return s;
          });
          const vpCheck = (pref: string, label: string) => (
            <FormControlLabel key={pref}
              control={<Checkbox size="small" checked={viewerPrefs.has(pref)} onChange={() => togglePref(pref)} disabled={!viewerCustomize} sx={{ p: 0.25 }} />}
              label={<Typography sx={{ fontSize: 11 }}>{label}</Typography>} />
          );
          return (
            <Box>
              <FormControlLabel
                control={<Checkbox size="small" checked={viewerCustomize} onChange={(e) => setViewerCustomize(e.target.checked)} sx={{ p: 0.25 }} />}
                label={<Typography sx={{ fontSize: 12, fontWeight: 600 }}>{isRadRes ? 'Customize viewer layout and report editor preferences' : 'Customize viewer layout preferences'}</Typography>}
              />

              <Divider sx={{ my: 1 }} />

              {/* Delivery Option */}
              <SectionLabel>Delivery Option</SectionLabel>
              <FormControl component="fieldset" disabled={!viewerCustomize} sx={{ mb: 1, pl: 1 }}>
                <RadioGroup value={deliveryVal} onChange={(e) => setDelivery(e.target.value)}>
                  <FormControlLabel value="DICOM" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>DICOM</Typography>} />
                  <FormControlLabel value="WORKS" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Works Diagnostic Viewer</Typography>} />
                  <FormControlLabel value="PACS_VIEW" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Pacs View</Typography>} />
                  <FormControlLabel value="OSIRIX" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Osirix</Typography>} />
                </RadioGroup>
              </FormControl>

              {/* Study open behavior */}
              {isRadRes && <>
                <Divider sx={{ my: 1 }} />
                <SectionLabel>Opening Study from Delivered Tab</SectionLabel>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, pl: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, mb: 0.25 }}>Unread / Unsigned</Typography>
                    <FormControl component="fieldset" disabled={!viewerCustomize}>
                      <RadioGroup value={unreadBehavior} onChange={(e) => setUnreadBehavior(e.target.value)}>
                        <FormControlLabel value="REPORT" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Add Report</Typography>} />
                        <FormControlLabel value="VIEWER" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>View Study</Typography>} />
                        <FormControlLabel value="DICTATE" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Launch Dictation</Typography>} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, mb: 0.25 }}>Read / Signed</Typography>
                    <FormControl component="fieldset" disabled={!viewerCustomize}>
                      <RadioGroup value={signedBehavior} onChange={(e) => setSignedBehavior(e.target.value)}>
                        <FormControlLabel value="REPORT" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Add Report</Typography>} />
                        <FormControlLabel value="VIEWER" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>View Study</Typography>} />
                        <FormControlLabel value="DICTATE" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Launch Dictation</Typography>} />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>
              </>}

              {/* Viewer Options */}
              <Divider sx={{ my: 1 }} />
              <SectionLabel>Viewer Options</SectionLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, pl: 1 }}>
                {vpCheck('USE_SCOUT_SERIES', 'Use Scout Series')}
                {vpCheck('SHOW_SYSTEM_MARKUPS', 'System Markups')}
                {vpCheck('SHOW_USER_MARKUPS', 'User Markups')}
                {vpCheck('LINK_SERIES', 'Link Series')}
                {vpCheck('SHOW_OVERLAYS', 'Overlays')}
                {vpCheck('FULL_SCREEN', 'Full Screen')}
              </Box>

              <Divider sx={{ my: 1 }} />
              <SectionLabel>Advanced Options</SectionLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, pl: 1 }}>
                {vpCheck('VIEWER_SCROLLNOSKIP', 'Smooth Scroll')}
                {vpCheck('VIEWER_SYNCINCINE', 'Sync During Cine')}
                {vpCheck('VIEWER_SCOUTINCINE', 'Scoutlines During Cine')}
                {vpCheck('VIEWER_CLIENTRENDER', 'Use Browser Render')}
                {vpCheck('VIEWER_CLIENTZOOM', 'Use Browser Zoom/Pan')}
                {vpCheck('VIEWER_PREFETCH', 'Use Aggressive Prefetch')}
                {vpCheck('SHOW_STATIC_US_FIRST', 'Show Static US Images First')}
                {vpCheck('SCROLL_US_STATIC_AS_ONE', 'Split Static/Multiframe US Series')}
                {vpCheck('CINE_STOP_AT_END', 'Stop Scroll At End of Series')}
              </Box>

              {/* Image Quality */}
              <Divider sx={{ my: 1 }} />
              <SectionLabel>Image Quality</SectionLabel>
              <FormControl component="fieldset" disabled={!viewerCustomize} sx={{ pl: 1 }}>
                <RadioGroup row value={iqVal} onChange={(e) => setIQ(e.target.value)}>
                  <FormControlLabel value="LOW" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Default Lossy</Typography>} />
                  <FormControlLabel value="HIGH" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>High Quality</Typography>} />
                  <FormControlLabel value="PNG" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Lossless PNG</Typography>} />
                  <FormControlLabel value="GIF" control={<Radio size="small" sx={{ p: 0.25 }} />} label={<Typography sx={{ fontSize: 11 }}>Lossless GIF</Typography>} />
                </RadioGroup>
              </FormControl>

              {/* PDF Background Color */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pl: 1 }}>
                <Typography sx={{ fontSize: 11 }}>PDF Background Color:</Typography>
                <TextField size="small" value={pdfBkColor} onChange={(e) => setPdfBkColor(e.target.value)}
                  disabled={!viewerCustomize} placeholder="FFFFFF"
                  inputProps={{ maxLength: 6, style: { fontFamily: 'monospace', fontSize: 11, width: 60 } }}
                  sx={{ '& .MuiInputBase-root': { fontSize: 11 } }} />
                <Box sx={{ width: 20, height: 20, border: '1px solid #999', borderRadius: '2px', backgroundColor: `#${pdfBkColor || 'FFF'}` }} />
              </Box>

              {/* Delay Options (Radiologist/Resident only) */}
              {isRadRes && <>
                <Divider sx={{ my: 1 }} />
                <SectionLabel>Signed Report Distribution Delay</SectionLabel>
                <FormControlLabel sx={{ pl: 1, mb: 0.5 }}
                  control={<Checkbox size="small" checked={customizeDelay} onChange={(e) => setCustomizeDelay(e.target.checked)} disabled={!viewerCustomize} sx={{ p: 0.25 }} />}
                  label={<Typography sx={{ fontSize: 11 }}>Customize Delay</Typography>}
                />
                {customizeDelay && viewerCustomize && (
                  <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 11, width: 100 }}>STROKE delay:</Typography>
                      <TextField size="small" value={strokeDelay} onChange={(e) => setStrokeDelay(e.target.value)}
                        type="number" inputProps={{ min: 0, max: 120, style: { fontSize: 11, width: 50 } }}
                        sx={{ '& .MuiInputBase-root': { fontSize: 11 } }} />
                      <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>sec (0–120)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 11, width: 100 }}>STAT delay:</Typography>
                      <TextField size="small" value={statDelay} onChange={(e) => setStatDelay(e.target.value)}
                        type="number" inputProps={{ min: 0, max: 120, style: { fontSize: 11, width: 50 } }}
                        sx={{ '& .MuiInputBase-root': { fontSize: 11 } }} />
                      <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>sec (0–120)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 11, width: 100 }}>Other delay:</Typography>
                      <TextField size="small" value={otherDelay} onChange={(e) => setOtherDelay(e.target.value)}
                        type="number" inputProps={{ min: 0, max: 300, style: { fontSize: 11, width: 50 } }}
                        sx={{ '& .MuiInputBase-root': { fontSize: 11 } }} />
                      <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>sec (0–300)</Typography>
                    </Box>
                  </Box>
                )}
              </>}

              {/* Report Editor Options (Radiologist/Resident only) */}
              {isRadRes && <>
                <Divider sx={{ my: 1 }} />
                <SectionLabel>Report Editor Options</SectionLabel>
                <Box sx={{ pl: 1 }}>
                  {vpCheck('RETURN_TO_WORKLIST', 'Return to Worklist after signing')}
                  {vpCheck('VIEW_SAVED_REPORT', 'View saved report after signing')}
                  {vpCheck('ADDENDUMS_AT_BEGIN_REPORT', 'Addendums at beginning of report')}
                  {vpCheck('ADDENDUMS_AT_END_REPORT', 'Addendums at end of report')}
                </Box>
              </>}

              {/* Study Viewer Log */}
              <Divider sx={{ my: 1 }} />
              <FormControlLabel sx={{ pl: 1 }}
                control={<Checkbox size="small" checked={enableStudyViewerLog} onChange={(e) => setEnableStudyViewerLog(e.target.checked)} sx={{ p: 0.25 }} />}
                label={<Typography sx={{ fontSize: 11 }}>Enable Study Viewer Log</Typography>}
              />
            </Box>
          );
        })()}

        {/* ══════════════════════ Monitor Config Tab ══════════════════════ */}
        {tab === 10 && (() => {
          const configJson = userConfigData ? JSON.stringify(userConfigData, null, 2) : '{}';
          return (
            <Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1.5 }}>
                {'Monitor configuration is a complex multi-monitor layout wizard. Below is the current raw configuration. Use the GWT UI for full editing.'}
              </Typography>
              {!isEdit ? (
                <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 2, textAlign: 'center' }}>Save the user first to view monitor configuration.</Typography>
              ) : (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'background.paper', maxHeight: 320, overflow: 'auto' }}>
                  <pre style={{ fontSize: 10, fontFamily: 'monospace', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'inherit' }}>
                    {configJson}
                  </pre>
                </Box>
              )}
            </Box>
          );
        })()}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={btnSx}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave}
          disabled={saving || !userId.trim() || !siteId.trim() || !lastName.trim()}
          sx={btnSx}>{saving ? 'Saving...' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Shared sub-components ── */
const FR = ({ l, children }: { l: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 110, textAlign: 'right', flexShrink: 0, pt: 0.5 }}>{l}:</Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5, mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{children}</Typography>
);

const fsx = { '& .MuiInputBase-root': { fontSize: 12 } };
const btnSx = { textTransform: 'none' as const, fontSize: 12 };
const tabSx = { textTransform: 'none' as const, fontSize: 12, minHeight: 36, py: 0 };

/* ── Permission groups mirroring GWT's Permissions enum grouping ── */
const PERM_GROUPS: { group: string; perms: string[] }[] = [
  { group: 'Studies', perms: [
    'DOWNLOAD_STUDIES', 'SEND_STUDIES_TO', 'UPLOAD_STUDIES', 'DOWNLOAD_FOR_CD', 'ADD_STUDY_SHARES',
    'COMPLETE_STUDIES', 'ASSIGN_STUDIES', 'RELEASE_STUDIES', 'ASSIGN_STUDIES_TO_RESIDENT',
    'HOLD_UNHOLD_STUDIES', 'MARK_READ', 'MARK_UNREAD', 'MARK_DICTATED', 'MARK_TRANSCRIBED',
    'MARK_INPROGRESS', 'EDIT_STUDIES', 'EDIT_NON_ASSIGNED_STUDIES',
    'DELETE_PURE_STUDIES', 'DELETE_ANY_STUDIES', 'DELETE_IMAGES_PURE_STUDIES', 'DELETE_IMAGES_ANY_STUDIES',
    'VIEW_STUDY_EVENTS', 'ASSIGN_TO_ME', 'EDIT_MISSED_SLA_REASONS', 'CALL_CONTACTS', 'AUTO_ADD_ORDERS',
    'VIEW_DICOM_DUMP', 'UPLOAD_MATCHING',
  ] },
  { group: 'Reports', perms: [
    'VIEW_DRAFT_REPORTS', 'ADD_DRAFT_REPORTS', 'SIGN_REPORTS', 'ADD_ADDENDUMS', 'SIGN_ADDENDUMS',
    'SEND_REPORTS_VIA_HL7', 'VIEW_FAX_QUEUE', 'TRANSCRIBE_REPORTS',
  ] },
  { group: 'Dictations', perms: ['DICTATE', 'DELETE_DICTATIONS'] },
  { group: 'Annotations', perms: ['CREATE_ANNOTATIONS', 'CLEAR_ANNOTATIONS'] },
  { group: 'Discordance', perms: ['VIEW_DISCORDANCE', 'ACKNOWLEDGE_DISCORDANCE', 'ADD_EDIT_ER_CALL', 'ADD_EDIT_RAD_CALL'] },
  { group: 'Peer Review', perms: ['PERFORM_REER_REVIEW', 'REQUEST_PEER_REVIEW'] },
  { group: 'Attachments', perms: ['ATTACH_FILES', 'DELETE_ATTACHMENTS', 'EDIT_LABEL_COMMENTS_ATTACHMENTS', 'ASSIGN_UNASSIGN_ATTACHMENTS'] },
  { group: 'Orders', perms: ['ADD_ORDERS', 'EDIT_ORDERS', 'DELETE_ORDERS', 'SEND_ORDERS_VIA_HL7', 'INSURANCE_AUTHORIZATION', 'ARRIVE_PATIENT', 'MATCH_WITH_ORDER'] },
  { group: 'Patients', perms: ['ADD_PATIENTS', 'EDIT_PATIENTS', 'DELETE_PATIENTS', 'PATIENT_DISCOVERY', 'EDIT_PATIENT_ID'] },
  { group: 'Notes', perms: [
    'VIEW_STUDY_NOTES', 'EDIT_STUDY_NOTES', 'DELETE_STUDY_NOTES',
    'VIEW_REPORT_NOTES', 'EDIT_REPORT_NOTES', 'DELETE_REPORT_NOTES',
    'VIEW_ORDER_NOTES', 'EDIT_ORDER_NOTES', 'DELETE_ORDER_NOTES',
    'VIEW_PRIVATE_NOTES',
  ] },
  { group: 'QC', perms: ['QC_OPEN_EDITOR', 'QC_MARK_UNMARK', 'QC_BREAK_SESSION'] },
  { group: 'Users/Roles', perms: [
    'ADD_EDIT_ADMINISTRATORS', 'DELETE_ADMINISTRATORS',
    'ADD_EDIT_REPORTING_PHYSICIANS', 'DELETE_REPORTING_PHYSICIANS',
    'ADD_EDIT_RESIDENTS', 'DELETE_RESIDENTS',
    'ADD_EDIT_MANAGERS', 'DELETE_MANAGERS',
    'ADD_EDIT_TECHS', 'DELETE_TECHS',
    'ADD_EDIT_TRANSCRIPTIONISTS', 'DELETE_TRANSCRIPTIONISTS',
    'ADD_EDIT_CLERKS', 'DELETE_CLERKS',
    'ADD_EDIT_NURSES', 'DELETE_NURSES',
    'ADD_EDIT_PHYSICIANS', 'DELETE_PHYSICIANS',
    'MOVE_USERS', 'MERGE_USERS',
  ] },
  { group: 'Sites', perms: ['ADD_EDIT_CUSTOM_ROLES', 'DELETE_CUSTOM_ROLES', 'EDIT_SYSTEM_ROLES'] },
  { group: 'Locations/Modalities', perms: ['ADD_EDIT_LOCATIONS', 'ADD_EDIT_MODALITIES', 'DELETE_LOCATIONS', 'DELETE_MODALITIES'] },
  { group: 'Insurance', perms: ['ADD_INSURANCE_COMPANY', 'EDIT_INSURANCE_COMPANY', 'DELETE_INSURANCE_COMPANY'] },
  { group: 'Quizzes', perms: ['CREATE_QUIZZES', 'TAKE_QUIZZES'] },
];

/* ══════════════════════════════════════════════════════════════════════════
 * ShuttlePanel — reusable Available ↔ Assigned two-list selector
 * Used by Access Groups and Custom Roles tabs
 * ══════════════════════════════════════════════════════════════════════════ */
interface ShuttleItem { key: string; label: string; sub?: string }
interface ShuttlePanelProps {
  title: string;
  availableLabel: string;
  assignedLabel: string;
  available: ShuttleItem[];
  assigned: ShuttleItem[];
  onAdd: (keys: string[]) => void;
  onRemove: (keys: string[]) => void;
}

const ShuttlePanel = ({ title, availableLabel, assignedLabel, available, assigned, onAdd, onRemove }: ShuttlePanelProps) => {
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState<Set<string>>(new Set());
  const [filterAvailable, setFilterAvailable] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');

  const filteredAvailable = filterAvailable
    ? available.filter((i) => i.label.toLowerCase().includes(filterAvailable.toLowerCase()) || (i.sub ?? '').toLowerCase().includes(filterAvailable.toLowerCase()))
    : available;
  const filteredAssigned = filterAssigned
    ? assigned.filter((i) => i.label.toLowerCase().includes(filterAssigned.toLowerCase()) || (i.sub ?? '').toLowerCase().includes(filterAssigned.toLowerCase()))
    : assigned;

  const handleAdd = () => { onAdd([...selectedAvailable]); setSelectedAvailable(new Set()); };
  const handleRemove = () => { onRemove([...selectedAssigned]); setSelectedAssigned(new Set()); };
  const handleAddAll = () => { onAdd(filteredAvailable.map((i) => i.key)); setSelectedAvailable(new Set()); };
  const handleRemoveAll = () => { onRemove(filteredAssigned.map((i) => i.key)); setSelectedAssigned(new Set()); };

  const toggleSel = (key: string, set: Set<string>, setter: React.Dispatch<React.SetStateAction<Set<string>>>) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    setter(next);
  };

  const listSx = { border: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: 180, maxHeight: 260, overflowY: 'auto' as const, bgcolor: 'background.paper' };

  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
        {`Move items between Available and ${title} lists. Changes are saved when you click Save.`}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'start' }}>
        {/* Available list */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>{availableLabel} ({available.length})</Typography>
          <TextField size="small" fullWidth placeholder="Filter…" value={filterAvailable}
            onChange={(e) => setFilterAvailable(e.target.value)} sx={{ mb: 0.5, '& .MuiInputBase-root': { fontSize: 11 } }} />
          <Box sx={listSx}>
            {filteredAvailable.map((item) => (
              <Box key={item.key} onClick={() => toggleSel(item.key, selectedAvailable, setSelectedAvailable)}
                sx={{ px: 1, py: 0.25, cursor: 'pointer', fontSize: 11,
                  backgroundColor: selectedAvailable.has(item.key) ? 'action.selected' : 'transparent',
                  '&:hover': { backgroundColor: selectedAvailable.has(item.key) ? 'action.focus' : 'action.hover' } }}>
                {item.label}
                {item.sub && <Typography component="span" sx={{ fontSize: 10, color: 'text.secondary', ml: 0.5 }}>({item.sub})</Typography>}
              </Box>
            ))}
            {filteredAvailable.length === 0 && (
              <Typography sx={{ fontSize: 11, color: 'text.disabled', p: 1, textAlign: 'center' }}>No items</Typography>
            )}
          </Box>
        </Box>

        {/* Shuttle buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 4 }}>
          <Button size="small" variant="outlined" onClick={handleAdd} disabled={selectedAvailable.size === 0}
            sx={{ fontSize: 10, minWidth: 32, px: 0.5 }}>&gt;</Button>
          <Button size="small" variant="outlined" onClick={handleAddAll} disabled={filteredAvailable.length === 0}
            sx={{ fontSize: 10, minWidth: 32, px: 0.5 }}>&gt;&gt;</Button>
          <Button size="small" variant="outlined" onClick={handleRemove} disabled={selectedAssigned.size === 0}
            sx={{ fontSize: 10, minWidth: 32, px: 0.5 }}>&lt;</Button>
          <Button size="small" variant="outlined" onClick={handleRemoveAll} disabled={filteredAssigned.length === 0}
            sx={{ fontSize: 10, minWidth: 32, px: 0.5 }}>&lt;&lt;</Button>
        </Box>

        {/* Assigned list */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>{assignedLabel} ({assigned.length})</Typography>
          <TextField size="small" fullWidth placeholder="Filter…" value={filterAssigned}
            onChange={(e) => setFilterAssigned(e.target.value)} sx={{ mb: 0.5, '& .MuiInputBase-root': { fontSize: 11 } }} />
          <Box sx={listSx}>
            {filteredAssigned.map((item) => (
              <Box key={item.key} onClick={() => toggleSel(item.key, selectedAssigned, setSelectedAssigned)}
                sx={{ px: 1, py: 0.25, cursor: 'pointer', fontSize: 11,
                  backgroundColor: selectedAssigned.has(item.key) ? 'action.selected' : 'transparent',
                  '&:hover': { backgroundColor: selectedAssigned.has(item.key) ? 'action.focus' : 'action.hover' } }}>
                {item.label}
                {item.sub && <Typography component="span" sx={{ fontSize: 10, color: 'text.secondary', ml: 0.5 }}>({item.sub})</Typography>}
              </Box>
            ))}
            {filteredAssigned.length === 0 && (
              <Typography sx={{ fontSize: 11, color: 'text.disabled', p: 1, textAlign: 'center' }}>No items</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const PermissionsTab = ({ user, sessionUser, userType }: { user: User | null; sessionUser: User | null; userType: string }) => {
  // Can only show permissions for the currently logged-in user (permissionsMap is only populated for session user)
  const isCurrentUser = !!user && !!sessionUser && (
    user.objKey === sessionUser.objKey ||
    user.user === sessionUser.user
  );

  // Collect all permissions across sites
  const allPerms = useMemo(() => {
    const source = isCurrentUser ? sessionUser : user;
    const pm = source?.permissionsMap;
    if (!pm) return new Set<string>();
    const s = new Set<string>();
    for (const perms of Object.values(pm)) {
      for (const p of perms) s.add(p);
    }
    return s;
  }, [user, sessionUser, isCurrentUser]);

  const siteKeys = useMemo(() => {
    const source = isCurrentUser ? sessionUser : user;
    return Object.keys(source?.permissionsMap ?? {});
  }, [user, sessionUser, isCurrentUser]);

  const hasPerms = allPerms.size > 0;
  const labelOf = (perm: string) => perm.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bReer\b/, 'Peer');

  return (
    <Box>
      <FR l="Role"><Typography sx={{ fontSize: 12, pt: 0.5 }}>{userType || '—'}</Typography></FR>
      {siteKeys.length > 0 && (
        <FR l="Sites">
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pt: 0.25 }}>
            {siteKeys.map((sk) => (
              <Chip key={sk} label={sk.replace('site:', '')} size="small" sx={{ fontSize: 10, height: 18 }} />
            ))}
          </Box>
        </FR>
      )}

      {!hasPerms && (
        <Box sx={{ mt: 2, p: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
            {isCurrentUser
              ? 'No permissions data available. This user may have unrestricted access (root admin).'
              : 'Permission details are only available for the currently logged-in user. Permissions are determined by the user\'s role and site configuration.'
            }
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
            Configure role permissions in the Sites panel → User Roles category.
          </Typography>
        </Box>
      )}

      {hasPerms && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
            Effective permissions (read-only). Role permissions are configured per-site.
          </Typography>
          {PERM_GROUPS.map(({ group, perms }) => {
            const granted = perms.filter((p) => allPerms.has(p));
            if (granted.length === 0) return null;
            return (
              <Box key={group} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 600, mb: 0.25 }}>
                  {group}
                  <Typography component="span" sx={{ fontSize: 10, color: 'text.secondary', ml: 0.5 }}>
                    ({granted.length}/{perms.length})
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {perms.map((p) => (
                    <Chip
                      key={p}
                      label={labelOf(p)}
                      size="small"
                      variant={allPerms.has(p) ? 'filled' : 'outlined'}
                      color={allPerms.has(p) ? 'success' : 'default'}
                      sx={{ fontSize: 9, height: 18, opacity: allPerms.has(p) ? 1 : 0.4 }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * MoveUserDialog — move a user to a different site
 * ══════════════════════════════════════════════════════════════════════════ */
const MoveUserDialog = ({ user, open, onClose, onSaved }: { user: User; open: boolean; onClose: () => void; onSaved: () => void }) => {
  const [destSite, setDestSite] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.getSites(),
    staleTime: 60_000,
  });

  const sites = useMemo(() => (sitesData?.records ?? []).filter((s) => !s.status || s.status === 'active'), [sitesData]);
  const userId = (user as Record<string, unknown>).userId as string ?? user.user;
  const currentSite = (user as Record<string, unknown>).siteid as string ?? user.siteId ?? '';

  const handleMove = async () => {
    if (!destSite) return;
    setSaving(true);
    try {
      await api.moveUser(user.objKey, `site:${destSite}`);
      pushSuccess(`User "${userId}" moved to site "${destSite}"`);
      onSaved();
    } catch (e) {
      pushError(`Failed to move user: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: 400, borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        Move User
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <FR l="User">{userId}</FR>
        <FR l="Current Site">{currentSite || '—'}</FR>
        <Divider sx={{ my: 1.5 }} />
        <FR l="Destination Site *">
          <TextField size="small" fullWidth select value={destSite} onChange={(e) => setDestSite(e.target.value)} sx={fsx}>
            <MenuItem value="">— Select —</MenuItem>
            {sites.filter((s) => s.siteId !== currentSite).map((s) => (
              <MenuItem key={s.siteId} value={s.siteId} sx={{ fontSize: 12 }}>{s.siteId} — {s.name}</MenuItem>
            ))}
          </TextField>
        </FR>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={btnSx}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleMove} disabled={saving || !destSite} sx={btnSx}>
          {saving ? 'Moving...' : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * MergeUsersDialog — merge one user into another
 * ══════════════════════════════════════════════════════════════════════════ */
const MergeUsersDialog = ({ user, allUsers, open, onClose, onSaved }: {
  user: User; allUsers: User[]; open: boolean; onClose: () => void; onSaved: () => void;
}) => {
  const [mergeInto, setMergeInto] = useState<User | null>(null);
  const [movePrefs, setMovePrefs] = useState(true);
  const [saving, setSaving] = useState(false);

  const userId = (user as Record<string, unknown>).userId as string ?? user.user;
  const otherUsers = useMemo(() => allUsers.filter((u) => u.objKey !== user.objKey), [allUsers, user.objKey]);

  const handleMerge = async () => {
    if (!mergeInto) return;
    setSaving(true);
    try {
      // baseUserKey = user to keep, originalUserKey = user to merge in (will be removed)
      await api.mergeUsers(mergeInto.objKey, user.objKey, undefined, movePrefs);
      pushSuccess(`User "${userId}" merged into "${mergeInto.user}"`);
      onSaved();
    } catch (e) {
      pushError(`Failed to merge users: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: 480, borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        Merge Users
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <Typography variant="body2" sx={{ fontSize: 11, color: 'text.secondary', mb: 1.5 }}>
          Merge will transfer all studies and data from the source user into the destination user. The source user will be removed.
        </Typography>
        <FR l="Source (remove)">{userId} ({user.name})</FR>
        <Divider sx={{ my: 1 }} />
        <FR l="Merge Into *">
          <Autocomplete
            size="small"
            options={otherUsers}
            getOptionLabel={(u) => `${(u as Record<string, unknown>).userId ?? u.user} — ${u.name}`}
            value={mergeInto}
            onChange={(_, v) => setMergeInto(v)}
            renderInput={(params) => <TextField {...params} placeholder="Search users..." sx={fsx} />}
            sx={{ fontSize: 12 }}
          />
        </FR>
        <FormControlLabel
          control={<Checkbox size="small" checked={movePrefs} onChange={(e) => setMovePrefs(e.target.checked)} sx={{ p: 0.25 }} />}
          label={<Typography sx={{ fontSize: 11 }}>Transfer preferences from source to destination</Typography>}
          sx={{ ml: '118px', mt: 0.5 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={btnSx}>Cancel</Button>
        <Button size="small" variant="contained" color="warning" onClick={handleMerge} disabled={saving || !mergeInto} sx={btnSx}>
          {saving ? 'Merging...' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
